import { Plane, Stamp, ShieldCheck, Briefcase, Check, Building, Trophy, Palmtree, Route, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { Overline, PageHero, CTABand } from "@/components/Bits";
import { HERO_IMG } from "@/lib/site";

const SERVICES = [
  {
    icon: Plane,
    title: "Air Ticketing",
    points: [
      "Domestic | International | SOTO",
      "Business Class · Premium Economy · Economy",
      "Priority routing and fare engineering for business needs",
    ],
  },
  {
    icon: Stamp,
    title: "Visa Assistance",
    points: [
      "ETA / PAR / Transit / Business / Conference / Family Visit",
      "Expert documentation review, embassy liaison, form filling, and appointment coordination",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Travel Insurance",
    points: [
      "Customized insurance plans for executives",
      "Includes Schengen and US travel policies",
    ],
  },
  {
    icon: Briefcase,
    title: "Executive Support",
    points: [
      "Itinerary design and urgent rerouting",
      "Multi-destination travel planning",
      "10–15 day credit facility for corporates",
    ],
  },
];

const INDUSTRIES = [
  { icon: Building, label: "Business / Trade" },
  { icon: Trophy, label: "Sports" },
  { icon: Palmtree, label: "Tourism (corporate & families)" },
  { icon: Route, label: "Transit" },
  { icon: Users, label: "Conference & Delegations" },
  { icon: Heart, label: "Family Visits (corporate associations)" },
];

export default function CorporateServices() {
  return (
    <div data-testid="corporate-services-page">
      <PageHero
        overline="Corporate Services"
        title="Precision travel management for corporates."
        subtitle="Four pillars of service, engineered to keep your teams moving efficiently across the globe."
        image={HERO_IMG}
      />

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24 md:pb-32">
        <RevealGroup className="grid gap-6 md:grid-cols-2">
          {SERVICES.map((s) => (
            <RevealItem key={s.title}>
              <Link
                to="/contact"
                data-testid={`corp-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="block h-full rounded-2xl border border-brand-blue/8 bg-white p-8 md:p-10 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer"
              >
                <div className="inline-flex rounded-xl bg-gradient-to-br from-brand-blue to-brand-ocean p-3.5 text-white">
                  <s.icon size={26} />
                </div>
                <h3 className="mt-6 text-2xl font-heading font-semibold text-brand-blue">{s.title}</h3>
                <ul className="mt-5 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 text-brand-slate">
                      <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
                        <Check size={13} strokeWidth={3} />
                      </span>
                      <span className="leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* Industries We Serve */}
      <section className="bg-white border-y border-brand-blue/5">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <Overline>Industries We Serve</Overline>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-brand-blue max-w-2xl">
              Built for the way corporates travel
            </h2>
          </Reveal>
          <RevealGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIES.map((it) => (
              <RevealItem key={it.label}>
                <Link to="/contact" data-testid={`industry-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-4 rounded-2xl border border-brand-blue/8 bg-brand-bg p-6 hover:border-brand-green/30 hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer">
                  <div className="inline-flex rounded-xl bg-brand-blue/5 p-3 text-brand-blue">
                    <it.icon size={24} />
                  </div>
                  <p className="font-heading font-medium text-brand-blue">{it.label}</p>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal delay={0.2}>
            <p className="mt-10 text-lg text-brand-slate max-w-3xl">
              We specialize in managing end-to-end travel for corporates and their teams worldwide.
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
